/**
 * Implements the core DataTable View used by read-only reports.
 */

import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import { errorParser } from "../../../utils";
import { config } from "../../../config/datatable";

import dtBs from "datatables.net-bs";
import "datatables.net-buttons-bs";
import "datatables.net-select";
import "datatables.net-colreorder";

import "../../../../../sass/chart-reviews.scss";
import "../../../../../sass/report-filters.scss";

const globalChannel = Radio.channel("global");
const reportChannel = Radio.channel("report");

const MAIN_CONTAINER_SELECTOR = "#content";
const DRILLDOWN_CONTAINER_SELECTOR = "div.tab-content";
const PARENT_CONTAINER_SELECTOR = ".table-container";
const BUTTONS_SELECTOR = ".col-xs-9";
const TOOLTIP_SELECTOR = "[data-toggle=tooltip]";
const POPOVER_SELECTOR = "[data-toggle=popover]";

const DEFAULTS = {};

class DataTable extends Marionette.ItemView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  initialize() {
    // localize options
    this.pageLength = window.RVC.pageLength || null;

    // disable DT based error handling
    // https://datatables.net/reference/option/%24.fn.dataTable.ext.errMode
    $.fn.dataTable.ext.errMode = "none";

    // add default button class names
    $.fn.dataTable.Buttons.defaults.dom.button.className =
      "btn btn-sm btn-emphasized";

    // BugzId 83775
    // plugin to enable external activation of processing indicator
    // https://datatables.net/plug-ins/api/processing()
    $.fn.dataTable.Api.register("processing()", function (show) {
      return this.iterator("table", (ctx) => {
        ctx.oApi._fnProcessingDisplay(ctx, show);
      });
    });

    this.listenTo(globalChannel, "content-resized", this._onContentResized);
    this.listenTo(reportChannel, "dt-ajax-stop", this._onButtonActionStop);

    reportChannel.reply("dt:columnOrder", this._getColumnOrder, this);
  }

  onBeforeRender() {
    this._applyBootstrapOverrides();
    this._applyConfiguration();
  }

  onShow() {
    // get reference to parent container
    this.$container = this.$el.parents(PARENT_CONTAINER_SELECTOR);
    // console.log(this.$container);

    // create a DT instance with its event handlers
    this.dt = this.$el
      // DT event handlers
      .on("column-reorder.dt", this._onColumnReordering.bind(this))
      .on("init.dt", this._onInit.bind(this))
      .on("draw.dt", this._onDraw.bind(this))
      .on("destroy.dt", this._onDestroy.bind(this))
      .on("xhr.dt", this._onAjaxCompleted.bind(this))
      .on("processing.dt", this._onProcessing.bind(this))
      .on("order.dt", this._onColumnOrder.bind(this))
      .on("error.dt", this._onError.bind(this))
      // DT instance
      .DataTable(this._dt_options);

    // console.log("this.dt", this.dt);
  }

  // runs before Marionette view is destroyed
  onBeforeDestroy() {
    // remove data rows
    this.dt.clear();
    // destroy DT instance
    this.dt.destroy();
    // delete instance reference
    delete this.dt;
  }

  onDestroy() {
    this.$el.remove();
  }

  // overrides the default DIV layout used for "results per page" and custom
  // buttons.
  _applyBootstrapOverrides() {
    $.extend(true, dtBs.defaults, {
      dom:
        "<'row dt-buttons'<'col-xs-3'l><'col-xs-9'f>>" +
        "<'row dt-body'<'col-xs-12'tr>>" +
        "<'row dt-footer'<'col-xs-5'i><'col-xs-7 right'p>>",
      renderer: "bootstrap",
    });
  }

  _applyConfiguration() {
    // merge default, custom options passed in or those set at the view level
    this._dt_options = Object.assign(
      {},
      config,
      this.options.config,
      this.dt_config
    );
    // console.info("DTCore Config", this._dt_options);

    if (this.pageLength) {
      this._dt_options.pageLength = this.pageLength;
    }

    // set vertical scrolling if not present
    if (!_.has(this._dt_options, "scrollY")) {
      // assume height as a pct of the #content flexbox div
      let height = Math.round($(MAIN_CONTAINER_SELECTOR).height() * 0.65);

      // unless it's a drilldown...
      if (this.options.isDrilldown) {
        height = Math.round($(DRILLDOWN_CONTAINER_SELECTOR).height() * 0.7);
      }

      this._dt_options.scrollY = height;
    }
  }

  _onContentResized() {
    // console.log("_onContentResized called", this);
    this.dt.columns.adjust();
  }

  /**
   * Deactivates processing mode for all current buttons
   */
  _onButtonActionStop() {
    this.dt.buttons().processing(false);
  }

  _getColumnOrder(asUrlParams = false) {
    return asUrlParams ? $.param(this.dt.columnOrder) : this.dt.columnOrder;
  }

  _onColumnReordering(e, settings, details) {
    // console.log("_onColumnReordering called", this);
    // only notify on final column move. this is a safeguard against a
    // misconfiguration to the default settings.
    // See http://datatables.net/reference/event/column-reorder
    if (details.drop) {
      // get list visible columns names
      const visibleColumns = _.pluck(
        _.where(settings.aoColumns, { visible: true }),
        "name"
      );

      const data = {
        route: this.route || {},
        columns: visibleColumns,
      };

      // notify reporting preferences module
      reportChannel.request("report-columns-moved", data);
    }
  }

  _onInit() {
    // console.log("_onInit called", this);

    $(".dataTables_processing", this.$container).css("display", "flex");

    // attach any configured buttons
    if (this.dt.buttons().length) {
      // look for location of buttons within container
      const $el = $(BUTTONS_SELECTOR, this.$container);

      // apply css class
      $el.addClass("text-right");

      // append buttons
      this.dt.buttons().container().appendTo($el);
    }
  }

  _onDraw() {
    // console.log("_onDraw called", this);

    // get scroll body
    const $scrollBody = $(".dataTables_scrollBody", this.$container);

    // get pagination details
    const info = this.dt.page.info();
    const $pagination = $(".dataTables_paginate", this.$container);

    // enable pagination region (in case it was hidden)
    $pagination.show();
    // hide pagination when only one page or no data are available
    if (info.pages < 2) {
      $pagination.hide();
    }

    // reduce height of scroll body after data has been rendered
    if ($scrollBody.length) {
      $scrollBody.css("min-height", "0");
    }

    // activate bootstrap tooltips within container
    $(TOOLTIP_SELECTOR, this.$container).tooltip({
      container: "body",
      html: false,
      placement: "auto top",
      trigger: "hover",
      delay: {
        show: 250,
        hide: 100,
      },
    });

    // activate bootstrap popover within container
    $(POPOVER_SELECTOR, this.$container).popover({
      container: "body",
      html: true,
      placement: "auto left",
      trigger: "hover",
      delay: {
        show: 250,
        hide: 100,
      },
    });

    // fade in the component
    // this.$container.fadeIn(250, this._onFadeIn.bind(this));
  }

  // _onFadeIn() {
  // 	this.dt.columns.adjust();
  // }

  // runs before DT instance is destroyed
  _onDestroy() {
    // console.log("_onDestroy called", this);
    // detach (delegated and named) event listeners
    this.dt.off(
      "**",
      "init.dt draw.dt destroy.dt xhr.dt processing.dt error.dt"
    );
  }

  _onAjaxCompleted(e, settings, json, jqXHR) {
    // console.log("_onAjaxCompleted called", this);
    if (jqXHR && jqXHR.statusText === "error") {
      // display error message
      globalChannel.trigger("status:show", {
        autoDismiss: false,
        type: "danger",
        message: errorParser(jqXHR),
      });
    }
  }

  _onProcessing(e, settings, processing) {
    // activate process div with display:flex instead of block, the default.
    $(".dataTables_processing", this.$container).css(
      "display",
      processing ? "flex" : "none"
    );
  }

  _onColumnOrder(e, settings) {
    // get current column order
    const columnSort = this.dt.order();

    // create column order object
    const columnOrder = {
      columns: settings.aoColumns.map((column) => ({ name: column.name })),
      order: columnSort.map((i) => ({ column: i[0], dir: i[1] })),
    };

    // attach object to DT instance
    this.dt.columnOrder = columnOrder;
  }

  _onError(e, settings, techNote, message) {
    const xhr = settings.jqXHR;
    // const json = xhr.responseJSON;

    console.warn("[Technote %s] %s", techNote, message);
    // console.warn(xhr);

    // display http status code based error message
    globalChannel.trigger("status:show", {
      autoDismiss: false,
      type: "danger",
      message: errorParser(xhr),
    });
  }
}

export { DataTable as default };
